import { apiClient, FilteredCreditApplication } from '@applyonline/shared'
import {
  MutationFunction,
  useMutation,
  useQueryClient
} from '@tanstack/react-query'
import { AxiosRequestConfig } from 'axios'
import {
  CreditApplication,
  PersonalInformationInput
} from '../types/Schemas/CreditApplicationSchema'
import { transformCreditToApi } from './credit-transform'

const SCOPE = 'credit-application'
const DETAIL = 'detail'

const keysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  allDetails: () => [{ scope: SCOPE, entity: DETAIL }] as const,
  detail: (id: string) => [{ scope: SCOPE, entity: DETAIL, id }] as const,
}

export interface SubmitCreditApplicationDto {
  recaptchaToken: string
  creditApplication: CreditApplication
}

const submitCreditApplication: MutationFunction<FilteredCreditApplication, SubmitCreditApplicationDto> = async (
  dto: SubmitCreditApplicationDto,
) => {
  const options: AxiosRequestConfig = {
    headers: {
      'x-recaptcha-token': dto.recaptchaToken,
    },
  }
  const creditApp = transformCreditToApi(dto.creditApplication)
  const response = await apiClient.post(
    `mechwarranty/creditApplication`,
    creditApp,
    options,
  )
  return response.data as FilteredCreditApplication
}

export function useSubmitCreditApplication(): [
  MutationFunction<FilteredCreditApplication, SubmitCreditApplicationDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset, isSuccess } = useMutation({
    mutationFn: submitCreditApplication,
    onSuccess: (data) => {
      queryClient.setQueryData(keysFactory.detail(data.id), data)
      return data
    },
  })

  return [mutateAsync, isPending, isSuccess, reset]
}

export interface UpdateCreditApplicationDto {
  creditApplicationId: string
  recaptchaToken: string
  personalInfoDto: PersonalInformationInput
}

const updateCreditApplication: MutationFunction<FilteredCreditApplication, UpdateCreditApplicationDto> = async (
  dto: UpdateCreditApplicationDto,
) => {
  const options: AxiosRequestConfig = {
    headers: {
      'x-recaptcha-token': dto.recaptchaToken,
    },
  }
  const response = await apiClient.put(
    `mechwarranty/creditApplication/${dto.creditApplicationId}`,
    dto.personalInfoDto.applicant,
    options,
  )
  return response.data as FilteredCreditApplication
}

export function useUpdateCreditApplication(): [
  MutationFunction<FilteredCreditApplication, UpdateCreditApplicationDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset, isSuccess } = useMutation({
    mutationFn: updateCreditApplication,
    onSuccess: (data) => {
      queryClient.setQueryData(keysFactory.detail(data.id), data)
      return data
    },
  })

  return [mutateAsync, isPending, isSuccess, reset]
}